// Session management utility for handling user inactivity and automatic logout
import { store } from '../store/store';
import { logout } from '../store/authSlice';
import soundManager from './soundManager';

class SessionManager {
  constructor() {
    this.inactivityTimer = null;
    this.inactivityTimeout = 60 * 60 * 1000; // 1 hour in milliseconds
    this.warningTimeout = 55 * 60 * 1000; // 55 minutes - show warning 5 minutes before logout
    this.warningTimer = null;
    this.isWarningShown = false;
    
    // Bind methods to preserve 'this' context
    this.resetTimer = this.resetTimer.bind(this);
    this.handleUserActivity = this.handleUserActivity.bind(this);
    this.logout = this.logout.bind(this);
    this.showWarning = this.showWarning.bind(this);
  }

  // Initialize session management
  init() {
    this.setupActivityListeners();
    this.resetTimer();
  }

  // Setup event listeners for user activity
  setupActivityListeners() {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, this.handleUserActivity, true);
    });
  }

  // Remove event listeners
  cleanup() {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.removeEventListener(event, this.handleUserActivity, true);
    });

    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
    
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
    }
  }

  // Handle user activity
  handleUserActivity() {
    // Only reset if user is logged in
    const state = store.getState();
    if (state.auth.userInfo && state.auth.userInfo.token) {
      this.resetTimer();
      
      // Hide warning if it was shown
      if (this.isWarningShown) {
        this.hideWarning();
      }
    }
  }

  // Reset inactivity timer
  resetTimer() {
    // Clear existing timers
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
    
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
    }

    // Set warning timer (5 minutes before logout)
    this.warningTimer = setTimeout(this.showWarning, this.warningTimeout);
    
    // Set logout timer (1 hour)
    this.inactivityTimer = setTimeout(this.logout, this.inactivityTimeout);
  }

  // Show inactivity warning
  showWarning() {
    this.isWarningShown = true;
    
    // Play warning sound
    soundManager.playWarning();
    
    // Create warning modal
    const warningModal = document.createElement('div');
    warningModal.id = 'inactivity-warning';
    warningModal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
    warningModal.innerHTML = `
      <div class="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
        <div class="flex items-center mb-4">
          <div class="bg-yellow-100 rounded-full p-2 mr-3">
            <svg class="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900">Session Expiring Soon</h3>
        </div>
        <p class="text-gray-600 mb-6">Your session will expire in 5 minutes due to inactivity. Click "Stay Logged In" to continue your session.</p>
        <div class="flex space-x-3">
          <button id="stay-logged-in" class="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            Stay Logged In
          </button>
          <button id="logout-now" class="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors">
            Logout Now
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(warningModal);

    // Add event listeners to buttons
    document.getElementById('stay-logged-in').addEventListener('click', () => {
      this.hideWarning();
      this.resetTimer();
    });

    document.getElementById('logout-now').addEventListener('click', () => {
      this.hideWarning();
      this.logout();
    });
  }

  // Hide inactivity warning
  hideWarning() {
    const warningModal = document.getElementById('inactivity-warning');
    if (warningModal) {
      warningModal.remove();
    }
    this.isWarningShown = false;
  }

  // Logout user due to inactivity
  logout() {
    this.hideWarning();
    this.cleanup();
    
    // Play logout sound
    soundManager.playError();
    
    // Show logout notification
    const logoutNotification = document.createElement('div');
    logoutNotification.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
    logoutNotification.innerHTML = `
      <div class="flex items-center">
        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        Session expired due to inactivity
      </div>
    `;
    
    document.body.appendChild(logoutNotification);
    
    // Remove notification after 3 seconds
    setTimeout(() => {
      if (logoutNotification.parentNode) {
        logoutNotification.remove();
      }
    }, 3000);

    // Dispatch logout action
    store.dispatch(logout());
    
    // Redirect to login page
    window.location.href = '/login';
  }

  // Check if session is still valid
  isSessionValid() {
    const state = store.getState();
    const userInfo = state.auth.userInfo;
    
    if (!userInfo || !userInfo.token) {
      return false;
    }

    try {
      const tokenPayload = JSON.parse(atob(userInfo.token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      
      return tokenPayload.exp > currentTime;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  }

  // Start session management
  start() {
    const state = store.getState();
    if (state.auth.userInfo && state.auth.userInfo.token) {
      if (this.isSessionValid()) {
        this.init();
      } else {
        this.logout();
      }
    }
  }

  // Stop session management
  stop() {
    this.cleanup();
  }
}

// Create singleton instance
const sessionManager = new SessionManager();

export default sessionManager;
